import { getActivityKey } from "../getActivityKey.js";
import dayjs from "dayjs";
import { ViewModel } from "mvc-kit";
import { castArray } from "lodash-es";
//#region src/modules/shared/ActivityFeed/viewmodels/ActivityFeedViewModel.ts
var DEFAULT_FILTERS = {
	dateTimeRange: {
		end: void 0,
		start: void 0
	},
	locationId: void 0,
	scope: void 0,
	userId: void 0
};
/** True when the user has constrained the feed beyond the page's location/user scope. */
function hasSearchFilters(filters) {
	return Boolean(filters.scope?.length || filters.dateTimeRange?.start || filters.dateTimeRange?.end || filters.locationId || filters.userId);
}
function createActivityFeedState(filters) {
	return {
		filters,
		items: [],
		noMoreSkips: false
	};
}
/**
* Remove items that resolve to the same React key, keeping the first occurrence.
* The server can return the same activity in overlapping pages (and pagination
* drifts once realtime items are inserted), which produces duplicate keys.
* Duplicate keys make React recreate/reorder DOM nodes on re-render, which
* resets the feed's scroll position — so the list must always be deduped.
*/
function dedupeByKey(items) {
	const seen = /* @__PURE__ */ new Set();
	const out = [];
	for (const item of items) {
		const key = getActivityKey(item);
		if (seen.has(key)) continue;
		seen.add(key);
		out.push(item);
	}
	return out;
}
var ActivityFeedViewModel = class extends ViewModel {
	deps;
	constructor(state, deps) {
		super(state);
		this.deps = deps;
	}
	/** Location filter precedence: explicit prop wins unless scoped to a user. */
	get locationIdArg() {
		if (this.deps.locationId && !this.deps.userId) return this.deps.locationId;
		return this.state.filters.locationId;
	}
	/** `[dateString, activities][]` sorted newest day first, filling empty days. */
	get groupedByDate() {
		const { items } = this.state;
		if (!items.length) return [];
		const newestDate = dayjs(items[0].timestamp).add(1, "day").set("hour", 0).toDate();
		const oldestDate = new Date(items.at(-1)?.timestamp ?? newestDate);
		const fmt = (d) => d.toLocaleDateString(void 0, {
			day: "numeric",
			month: "numeric",
			year: "numeric"
		});
		const dateMap = {};
		let cursor = new Date(oldestDate);
		while (cursor <= newestDate) {
			dateMap[fmt(cursor)] = [];
			const next = new Date(cursor);
			next.setDate(next.getDate() + 1);
			cursor = next;
		}
		items.forEach((it) => {
			const key = fmt(new Date(it.timestamp));
			if (dateMap[key]) dateMap[key].push(it);
		});
		return Object.entries(dateMap).sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime());
	}
	onInit() {
		this.loadInitial().catch(() => {});
	}
	async loadInitial() {
		this.set({ noMoreSkips: false });
		const { activity, hasMore } = await this.deps.service.getActivityFeed(this.buildArgs(0), this.disposeSignal);
		this.set({
			items: dedupeByKey(activity),
			noMoreSkips: !hasMore
		});
	}
	async loadMore() {
		if (this.state.noMoreSkips) return;
		const { activity, hasMore } = await this.deps.service.getActivityFeed(this.buildArgs(this.state.items.length), this.disposeSignal);
		this.set({
			items: dedupeByKey([...this.state.items, ...activity]),
			noMoreSkips: !hasMore
		});
	}
	insertRealtimeItem(newItem) {
		if (hasSearchFilters(this.state.filters)) return;
		const { locationId, userId } = this.deps;
		if (userId && newItem.user?.id !== userId) return;
		if (locationId && !castArray(newItem.locationId).includes(locationId)) return;
		const newKey = getActivityKey(newItem);
		if (this.state.items.some((it) => getActivityKey(it) === newKey)) return;
		const items = [...this.state.items];
		let i = 0;
		while (i < items.length && new Date(newItem.timestamp) < new Date(items[i].timestamp)) i++;
		items.splice(i, 0, newItem);
		this.set({ items });
	}
	setFilters(partial) {
		const filters = {
			...this.state.filters,
			...partial
		};
		this.set({
			filters,
			noMoreSkips: false
		});
		this.deps.onFiltersChange?.(filters);
		this.loadInitial().catch(() => {});
	}
	buildArgs(skip) {
		const { filters } = this.state;
		return {
			detailScope: filters.scope?.length ? filters.scope : void 0,
			fromDate: filters.dateTimeRange?.start,
			locationId: this.locationIdArg,
			pagination: {
				limit: 20,
				skip
			},
			toDate: filters.dateTimeRange?.end,
			userId: this.deps.userId || filters.userId
		};
	}
};
//#endregion
export { ActivityFeedViewModel, DEFAULT_FILTERS, createActivityFeedState, hasSearchFilters };

//# sourceMappingURL=ActivityFeedViewModel.js.map